import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lightweight client-side record of an active PM full-access impersonation so
// the banner can show "Acting as [User] for [Company]" (the company name is not
// carried in the JWT). Cleared when the admin returns to their own session.
const CTX_KEY = 'nexflow.pm_impersonation';

export const getPmImpersonationContext = () => {
  try {
    const raw = localStorage.getItem(CTX_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const setPmImpersonationContext = (ctx) => {
  if (ctx) localStorage.setItem(CTX_KEY, JSON.stringify(ctx));
  else localStorage.removeItem(CTX_KEY);
};

export const clearPmImpersonationContext = () => setPmImpersonationContext(null);

/**
 * Start an ADMIN FULL-ACCESS impersonation of a PM portal user using the
 * existing JWT `imp` mechanism. `userLike`: { id, name, email, role, company }.
 * Requires a confirmation. On success, a full reload lands on the PM portal so
 * AuthContext + every route guard re-reads the impersonated session cookies.
 */
export async function startPmImpersonation(userLike) {
  if (!userLike?.id) {
    toast.error('Missing user id');
    return;
  }
  const company = userLike.company || '';
  const confirmMsg =
    `Open ${userLike.name}'s Property Management portal with FULL ACCESS?\n\n` +
    `You will act AS ${userLike.name}${company ? ` for ${company}` : ''} using THEIR exact permissions — ` +
    `no admin override. Every action is logged under your admin identity. ` +
    `Use "Return to Admin" in the banner to exit.`;
  if (!window.confirm(confirmMsg)) return;
  try {
    const { data } = await axios.post(
      `${API}/admin/pm/users/${userLike.id}/impersonate`,
      {},
      { withCredentials: true },
    );
    setPmImpersonationContext({
      user_id: userLike.id,
      name: data?.as_user?.name || userLike.name,
      role: data?.as_user?.role || userLike.role,
      company: data?.company?.name || company,
    });
    toast.success(`Full access — acting as ${userLike.name}`);
    // Full reload so AuthContext + route guards re-init under the impersonated JWT.
    window.location.href = '/pm/dashboard';
  } catch (err) {
    const detail = err.response?.data?.detail;
    toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Failed to open full access');
  }
}
